import { Redirect, router } from 'expo-router';
import { useAtom, useAtomValue } from 'jotai';
import * as React from 'react';
import { useState } from 'react';
import { Image } from 'react-native';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FruitfulCustomIcon,
    isWeb,
    LabelEmphasized,
    ScreenContainer,
    ScrollView,
    Separator,
    Spinner,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import Arrow from '../../../../assets/images/banking/paycheck-routing/arrow.png';
import ArrowShort from '../../../../assets/images/banking/paycheck-routing/arrow-short.png';
import Line from '../../../../assets/images/banking/paycheck-routing/line.png';
import LineShort from '../../../../assets/images/banking/paycheck-routing/line-short.png';
import { getDefaultedAccountNickname } from '../../../common/utils/bankingUtils';
import { captureException } from '../../../logger/Logger';
import useShouldSeeIncomeSplit from '../../hooks/income-split/useShouldSeeIncomeSplit';
import useGetExistingPaycheckConfigs from '../../hooks/paycheck-routing/useGetPaycheckConfigs';
import useSavePaycheckConfig from '../../hooks/paycheck-routing/useSavePaycheckConfig';
import {
    currentlyEditingConfigAtom,
    selectedIncomeAtom,
    selectedIncomeBankAccountAtom,
} from '../../state/PaycheckRoutingAtoms';
import { bankAccountDisplayDetails } from '../../state/SpacesAtoms';
import {
    type PaycheckRoutingConfig,
    type PaycheckRoutingRule,
    PaycheckRoutingRuleType,
    PaycheckRoutingStatus,
} from '../../types/PaycheckRouting';
import { BankDisclosureShortFootnote } from '../Disclosures';
import RuleSummary from './common/RuleSummary';

const Arrows = ({ rules }: { rules: PaycheckRoutingRule[] }) => {
    return rules.map((_, i) => {
        return (
            <View key={i}>
                <Image
                    source={i === 0 ? Arrow : ArrowShort}
                    style={{
                        height: i === 0 ? 80 : 46,
                        resizeMode: 'stretch',
                    }}
                />
                {i < rules.length - 1 && (
                    <Image
                        source={Line}
                        style={{
                            height: 42,
                        }}
                    />
                )}
            </View>
        );
    });
};

const IncomeSummary = () => {
    const selectedIncome = useAtomValue(selectedIncomeAtom);
    const selectedIncomeBankAccount = useAtomValue(
        selectedIncomeBankAccountAtom,
    );

    const { originator } = selectedIncome ?? {};
    const { icon, color, nickname, accountType, jointUser } =
        selectedIncomeBankAccount ?? {};
    const accountDetails = { accountType, nickname, jointUser };
    return (
        <YStack gap={20}>
            <LabelEmphasized color="$gray1100" textTransform="uppercase">
                when this income arrives
            </LabelEmphasized>
            <XStack justifyContent="space-between" alignItems="center">
                <XStack gap={8} alignItems="center">
                    <FruitfulCustomIcon
                        icon={icon}
                        color={color}
                        customSize={{ iconSize: 20, padding: 16 }}
                    />
                    <YStack gap={4}>
                        <BodyMediumRegular>{originator}</BodyMediumRegular>
                        <BodySmallRegular color="$gray1000">
                            {getDefaultedAccountNickname(accountDetails)}{' '}
                            account
                        </BodySmallRegular>
                    </YStack>
                </XStack>
            </XStack>
        </YStack>
    );
};

const RulesSummary = () => {
    const currentlyEditingConfig = useAtomValue(currentlyEditingConfigAtom);
    const accountDisplayDetails = useAtomValue(bankAccountDisplayDetails);

    return (
        <YStack>
            <IncomeSummary />
            <XStack width="100%">
                <YStack marginLeft={16}>
                    <Arrows
                        rules={currentlyEditingConfig?.rules?.primary ?? []}
                    />
                    <Image
                        source={LineShort}
                        style={{
                            height: 26,
                            resizeMode: 'stretch',
                        }}
                    />
                    <Arrows
                        rules={currentlyEditingConfig?.rules?.remaining ?? []}
                    />
                </YStack>
                <YStack gap={20} marginTop={16}>
                    <LabelEmphasized
                        color="$gray1100"
                        textTransform="uppercase"
                    >
                        these transfers will initiate
                    </LabelEmphasized>
                    <YStack gap={36}>
                        {currentlyEditingConfig?.rules?.primary.map(
                            (rule, i) => {
                                const account =
                                    rule.accountMetadata ??
                                    accountDisplayDetails.find(
                                        (account) =>
                                            account.bankAccountId ===
                                            rule.accountId,
                                    );
                                return account ? (
                                    <View marginLeft={8} key={i}>
                                        <RuleSummary
                                            key={i}
                                            rule={rule}
                                            account={account}
                                        />
                                    </View>
                                ) : null;
                            },
                        )}
                    </YStack>
                    <LabelEmphasized
                        color="$gray1100"
                        textTransform="uppercase"
                    >
                        remainder goes to
                    </LabelEmphasized>
                    <YStack gap={36}>
                        {currentlyEditingConfig?.rules?.remaining.map(
                            (rule, i) => {
                                const account =
                                    rule.accountMetadata ??
                                    accountDisplayDetails.find(
                                        (account) =>
                                            account.bankAccountId ===
                                            rule.accountId,
                                    );
                                return account ? (
                                    <View marginLeft={8} key={i}>
                                        <RuleSummary
                                            key={i}
                                            rule={rule}
                                            account={account}
                                        />
                                    </View>
                                ) : null;
                            },
                        )}
                    </YStack>
                </YStack>
            </XStack>
        </YStack>
    );
};

const ReviewRouting = () => {
    const [isSavingConfig, setIsSavingConfig] = useState(false);
    const [currentlyEditingConfig, setCurrentlyEditingConfig] = useAtom(
        currentlyEditingConfigAtom,
    );
    const selectedIncome = useAtomValue(selectedIncomeAtom);

    const { mutate: createPaycheckConfig } = useSavePaycheckConfig();
    const toast = useToastController();
    const { refetch, paycheckRoutingConfigs } = useGetExistingPaycheckConfigs();

    const shouldSeeIncomeSplit = useShouldSeeIncomeSplit();

    if (shouldSeeIncomeSplit) {
        return <Redirect href="/home/banking" />;
    }

    const paycheckSplitExists =
        paycheckRoutingConfigs?.some(
            (config) => config.accountId === currentlyEditingConfig?.accountId,
        ) ?? false;

    return (
        <ScreenContainer
            title="Review and agree"
            hasBackButton
            onFallbackRoute="/home/banking"
            onBack={() => {
                router.back();
            }}
        >
            <YStack gap={24} justifyContent="space-between">
                <ScrollView showsVerticalScrollIndicator={isWeb}>
                    <YStack gap={24} marginBottom={24}>
                        <BodyMediumRegular color="$gray1000">
                            Starting with your next deposit, transfers will
                            process from top to bottom subject to available
                            funds.
                        </BodyMediumRegular>
                        <RulesSummary />
                        <Separator />
                        <BodySmallRegular>
                            By confirming below, I authorize Fruitful to
                            initiate the transfers of my money as indicated
                            above.
                        </BodySmallRegular>
                    </YStack>
                </ScrollView>

                <YStack gap={12}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text={isSavingConfig ? '' : 'Confirm'}
                        icon={isSavingConfig ? <Spinner /> : null}
                        onPress={() => {
                            // Update the atom with new rules, keeping the rest of the config the same
                            setCurrentlyEditingConfig({
                                ...currentlyEditingConfig,
                                rules: {
                                    primary:
                                        currentlyEditingConfig?.rules
                                            ?.primary ?? [],
                                    remaining:
                                        currentlyEditingConfig?.rules
                                            ?.remaining ?? [],
                                },
                            });

                            const { rules } = currentlyEditingConfig;

                            // Combine primary and remaining into one routingRules array
                            const combinedRules = [
                                ...(rules?.primary ?? []),
                                ...(rules?.remaining ?? []),
                            ];

                            // Create a new object omitting the old rules and adding routingRules
                            const updatedConfig: Partial<PaycheckRoutingConfig> =
                                {
                                    accountId: selectedIncome?.accountId ?? '',
                                    originator:
                                        selectedIncome?.originator ?? '',
                                    receiver: selectedIncome?.receiver ?? '',
                                    isActive:
                                        currentlyEditingConfig?.status ===
                                        PaycheckRoutingStatus.ACTIVE,
                                    status: currentlyEditingConfig?.status,
                                    rules: combinedRules,
                                };

                            updatedConfig.rules = combinedRules.map(
                                (rule, index) => {
                                    const { accountMetadata, ...rest } = rule;

                                    const transformedAmount =
                                        rule.type !==
                                        PaycheckRoutingRuleType.PERCENTAGE
                                            ? rule.amount * 100
                                            : rule.amount;
                                    return {
                                        ...rest,
                                        amount: transformedAmount,
                                        order: index,
                                    };
                                },
                            );

                            if (
                                currentlyEditingConfig?.id !== null &&
                                currentlyEditingConfig?.id !== undefined
                            ) {
                                updatedConfig.id = currentlyEditingConfig.id;
                                updatedConfig.rules = updatedConfig.rules.map(
                                    (rule) => {
                                        return {
                                            ...rule,
                                            paycheckRoutingConfigId:
                                                currentlyEditingConfig.id,
                                        };
                                    },
                                );
                            }

                            setIsSavingConfig(true);
                            createPaycheckConfig(
                                {
                                    body: updatedConfig,
                                },
                                {
                                    onSuccess: async () => {
                                        await refetch();
                                        setCurrentlyEditingConfig({});
                                        setIsSavingConfig(false);
                                        router.navigate(
                                            `/banking/paycheck-routing/routing-confirmation?isEdit=${paycheckSplitExists}`,
                                        );
                                    },
                                    onError: (error) => {
                                        setIsSavingConfig(false);
                                        toast.show(
                                            'Error saving paycheck routing rules',
                                        );
                                        captureException(error);
                                    },
                                },
                            );
                        }}
                    />
                    <BankDisclosureShortFootnote />
                </YStack>
            </YStack>
        </ScreenContainer>
    );
};

export default ReviewRouting;
